import { AuthState } from '@eih-netbroker/common';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useLocale } from './context/NbLocalizationContext';
import { GlobalErrorBoundary } from './modules/core/pages/GlobalErrorBoundary';
import { Frame } from './modules/core/partials/Frame';

import { JSX, lazy, Suspense } from 'react';
import { AppRedirectPage } from './modules/app-redirect/pages/AppRedirectPage';
import { AuthorizePage } from './modules/login/pages/AuthorizePage';
import { LoginPage } from './modules/login/pages/LoginPage';
import { NotFoundPage } from './modules/not-found/pages/NotFoundPage';
import { BETFillListProvider } from './modules/trading/pages/BETFillListContext';
import { LazyChart } from './shared/components/chartIq/LazyChart';
import { FullScreenLoader } from './shared/components/fullscreen-loader/FullScreenLoader';
import { NbLoader } from './shared/components/loader/Loader';
import { routes } from './shared/data/routes';
import { useAuthorizationFlow } from './shared/services/authorization/useAuthorizationFlow';

const HomePage = lazy(() => import('./modules/home/pages/HomePage'));
const TradingPage = lazy(() => import('./modules/trading/pages/TradingPage'));
const WatchlistPage = lazy(() => import('./modules/watchlist/pages/WatchlistPage'));
const UserSettingsPage = lazy(() => import('./modules/settings/pages/UserSettingsPage'));
const DataServiceManagementPage = lazy(() => import('./modules/settings/pages/DataServiceManagementPage'));
const InterfaceSettingsPage = lazy(() => import('./modules/settings/pages/InterfaceSettingsPage'));
const PortfolioPage = lazy(() => import('./modules/portfolio/pages/PortfolioPage'));

const LazyPage = (child: JSX.Element) => {
  return (
    <Suspense
      fallback={
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      }
    >
      {child}
    </Suspense>
  );
};

const AuthenticatedLayout = (_: { authStatus: AuthState }) => {
  const { translation } = useLocale();

  return (
    <GlobalErrorBoundary
      errorTitle={translation.errorWidgetContent.title}
      errorText={translation.errorWidgetContent.error}
      buttonText={translation.errorWidgetContent.reload}
      isFullScreen
    >
      <Frame children={<Outlet />} />
    </GlobalErrorBoundary>
  );
};

const NotAuthenticatedAppRouting = () => {
  return (
    <Routes>
      <Route
        path={routes.login}
        element={LazyPage(<LoginPage />)}
      />
      <Route
        path={routes.authorize}
        element={<AuthorizePage />}
      />
      <Route
        path={routes.app}
        element={<AppRedirectPage />}
      />
      <Route
        path={'/'}
        element={LazyPage(<LoginPage />)}
      />
      <Route
        path="*"
        element={
          <Navigate
            to={routes.login}
            replace
          />
        }
      />
    </Routes>
  );
};

const AppRouting = () => {
  const { appAuth } = useAuthorizationFlow();

  return (
    <>
      {[AuthState.NotAuthenticated].includes(appAuth.authStatus) && <NotAuthenticatedAppRouting />}

      {[AuthState.SignOff, AuthState.Loading, AuthState.Unknown].includes(appAuth.authStatus) && <FullScreenLoader />}

      {appAuth.authStatus === AuthState.Authenticated && (
        <Routes>
          <Route
            path={routes.login}
            element={LazyPage(<LoginPage />)}
          />

          <Route
            path={routes.authorize}
            element={<AuthorizePage />}
          />

          <Route
            path={routes.app}
            element={<AppRedirectPage />}
          />

          <Route element={<AuthenticatedLayout authStatus={appAuth.authStatus} />}>
            <Route
              path="/"
              element={<Navigate to={routes.home} />}
            />

            <Route
              path={routes.home}
              element={LazyPage(<HomePage />)}
            />

            <Route
              path={routes.chart}
              element={<LazyChart type={'page'} />}
            />

            <Route
              path={routes.userSettings}
              element={LazyPage(<UserSettingsPage />)}
            />

            <Route
              path={routes.dataServiceManagement}
              element={LazyPage(<DataServiceManagementPage />)}
            />

            <Route
              path={routes.siteSettings}
              element={LazyPage(<InterfaceSettingsPage />)}
            />

            <Route
              path={routes.watchlist}
              element={LazyPage(<WatchlistPage />)}
            />

            <Route
              path={routes.portfolio}
              element={LazyPage(<PortfolioPage />)}
            />

            <Route
              path={routes.trading}
              element={LazyPage(
                <BETFillListProvider>
                  <TradingPage />
                </BETFillListProvider>,
              )}
            />

            <Route
              path="*"
              element={<NotFoundPage />}
            />
          </Route>
        </Routes>
      )}
    </>
  );
};

export default AppRouting;
